<!-- == Unichat Extension ==
     Modal para gerenciar Setores (Times) vinculados a uma Secretaria.
     Permite adicionar e remover vínculos Secretaria ↔ Setor.
-->
<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store.js';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { getSectorTeams, addSectorTeam, removeSectorTeam } from 'dashboard/unichat/api/unichat.js';

const props = defineProps({
  sector: { type: Object, required: true },
  accountId: { type: [Number, String], required: true },
});

const emit = defineEmits(['close']);

// Times nativos do Chatwoot (Setores)
const allTeams = useMapGetter('teams/getTeams');

const linkedTeams = ref([]);
const isLoading = ref(false);
const addingTeamId = ref(null);
const removingTeamId = ref(null);

// Times ainda não vinculados a esta secretaria
const unlinkedTeams = computed(() => {
  const linkedIds = new Set(linkedTeams.value.map(t => t.id));
  return (allTeams.value || []).filter(t => !linkedIds.has(t.id));
});

const fetchLinkedTeams = async () => {
  isLoading.value = true;
  try {
    const { data } = await getSectorTeams(props.accountId, props.sector.id);
    linkedTeams.value = Array.isArray(data) ? data : [];
  } catch {
    useAlert('Erro ao carregar setores vinculados.');
  } finally {
    isLoading.value = false;
  }
};

const onAdd = async (team) => {
  addingTeamId.value = team.id;
  try {
    await addSectorTeam(props.accountId, props.sector.id, team.id);
    // Recarrega lista para refletir estado real do banco
    await fetchLinkedTeams();
    useAlert(`Setor "${team.name}" vinculado com sucesso.`);
  } catch (err) {
    const status = err?.response?.status;
    if (status === 422) {
      // Já estava vinculado — recarrega para mostrar estado correto
      await fetchLinkedTeams();
      useAlert(`Setor "${team.name}" já estava vinculado.`);
    } else {
      useAlert(`Erro ao vincular setor "${team.name}".`);
    }
  } finally {
    addingTeamId.value = null;
  }
};

const onRemove = async (team) => {
  removingTeamId.value = team.id;
  try {
    await removeSectorTeam(props.accountId, props.sector.id, team.id);
    linkedTeams.value = linkedTeams.value.filter(t => t.id !== team.id);
    useAlert(`Setor "${team.name}" desvinculado.`);
  } catch {
    useAlert(`Erro ao desvincular setor "${team.name}".`);
  } finally {
    removingTeamId.value = null;
  }
};

onMounted(fetchLinkedTeams);
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    @click.self="emit('close')"
  >
    <div class="bg-n-surface-1 rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6 flex flex-col gap-5">

      <!-- Título -->
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-heading-2 text-n-slate-12">Setores da Secretaria</h3>
          <p class="text-sm text-n-slate-9 mt-0.5">{{ sector.name }}</p>
        </div>
        <button class="text-n-slate-11 hover:text-n-slate-12" @click="emit('close')">✕</button>
      </div>

      <!-- Setores vinculados -->
      <div>
        <p class="text-sm font-medium text-n-slate-12 mb-2">Setores vinculados</p>

        <div v-if="isLoading" class="text-sm text-n-slate-9 py-2">Carregando...</div>

        <div v-else-if="!linkedTeams.length"
          class="text-sm text-n-slate-9 border border-dashed border-n-weak rounded-lg px-3 py-3">
          Nenhum setor vinculado ainda.
        </div>

        <div v-else class="flex flex-col gap-1">
          <div
            v-for="team in linkedTeams"
            :key="team.id"
            class="flex items-center justify-between px-3 py-2 rounded-lg bg-n-surface-2"
          >
            <div class="flex items-center gap-2">
              <Icon icon="i-lucide-users" class="size-4 text-n-slate-11" />
              <span class="text-sm text-n-slate-12">{{ team.name }}</span>
            </div>
            <Button
              v-tooltip.top="'Desvincular'"
              icon="i-woot-bin"
              slate
              sm
              :is-loading="removingTeamId === team.id"
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="onRemove(team)"
            />
          </div>
        </div>
      </div>

      <!-- Divisor -->
      <div v-if="unlinkedTeams.length" class="border-t border-n-weak" />

      <!-- Setores disponíveis para vincular -->
      <div v-if="unlinkedTeams.length">
        <p class="text-sm font-medium text-n-slate-12 mb-2">Vincular setor existente</p>
        <div class="flex flex-col gap-1 max-h-48 overflow-y-auto">
          <div
            v-for="team in unlinkedTeams"
            :key="team.id"
            class="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-n-surface-2 transition-colors"
          >
            <div class="flex items-center gap-2">
              <Icon icon="i-lucide-users" class="size-4 text-n-slate-9" />
              <span class="text-sm text-n-slate-11">{{ team.name }}</span>
            </div>
            <Button
              label="Vincular"
              size="sm"
              :is-loading="addingTeamId === team.id"
              @click="onAdd(team)"
            />
          </div>
        </div>
      </div>

      <!-- Fechar -->
      <div class="flex justify-end pt-1">
        <Button label="Fechar" variant="clear" @click="emit('close')" />
      </div>
    </div>
  </div>
</template>
